import { useCallback, useRef, useState } from "react";

/**
 * Drop-in replacement for antd's `message.useMessage()`.
 *
 * Only `open({ type, content })` and the context holder were ever used, but
 * pulling `message` in forced the whole antd runtime — theme engine, cssinjs,
 * rc-* helpers — into the entry chunk, because it is a hook and so cannot be
 * deferred to the click that needs it. This keeps the same call signature so
 * the call sites are unchanged.
 *
 *   const [messageApi, contextHolder] = useToast();
 *   messageApi.open({ type: "success", content: "Saved" });
 *   return <>{contextHolder}...</>;
 */
const DURATION_MS = 3000;

export default function useToast() {
  const [toasts, setToasts] = useState([]);
  const nextId = useRef(0);

  const open = useCallback(({ type = "info", content = "", duration } = {}) => {
    const id = nextId.current++;
    setToasts((prev) => [...prev, { id, type, content }]);
    setTimeout(
      () => setToasts((prev) => prev.filter((t) => t.id !== id)),
      typeof duration === "number" ? duration * 1000 : DURATION_MS
    );
  }, []);

  // antd exposes shorthands alongside open(); mirror the common ones so a
  // future call site does not silently break.
  const api = useRef(null);
  if (!api.current) {
    api.current = {
      open,
      success: (content) => open({ type: "success", content }),
      error: (content) => open({ type: "error", content }),
      info: (content) => open({ type: "info", content }),
      warning: (content) => open({ type: "warning", content }),
    };
  }

  const contextHolder = (
    <div className="toast-stack" role="status" aria-live="polite">
      {toasts.map((t) => (
        <div key={t.id} className={`toast toast--${t.type}`}>
          <span className="toast__dot" aria-hidden="true" />
          {t.content}
        </div>
      ))}
    </div>
  );

  return [api.current, contextHolder];
}
